import { useContext, useEffect, useState } from "react";
import { useStepData } from "../../../stepper/UseStepData";
import { useInputHandler } from "../../../stepper/hooks/useInouthandler";
import { StepSubmitter } from "../../../stepper/StepSubmitter";
import { StepperContext } from "../../../stepper/context/StepperContext";
import { useDispatch } from "react-redux";

import {
  Box,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import TitleBar from "../../../components/common-components/TitleBar";
import { createUpdateItin } from "../../../stores/itin";
import styled from "styled-components";
import ApiHelperFunction from "../../../api";
import ListLoadingNew from "../../../components/common-components/ListLoadingNew";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { setToast } from "../../../stores/toast/toastslice";

const StyledFormControlLabel = styled(FormControlLabel)({
  borderRadius: "8px",
  background: "white",
  padding: "10px",
  marginBottom: "10px",
  boxShadow:
    "0px 1px 2px 0px #00000005, 0px 3px 3px 0px #00000005, 0px 7px 4px 0px #00000003, 0px 12px 5px 0px #00000000, 0px 18px 5px 0px #00000000",
  "& .MuiFormControlLabel-label": {
    color: "#2C3034",
    fontSize: "16px",
    fontWeight: "500",
  },
});

export const Step1 = () => {
  const navigate = useNavigate();
  const stepperState = useContext(StepperContext);
  const { data } = useStepData();
  const { payload, handleChange, setData } = useInputHandler<any>(data);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("user_token");
  const [getLoading, setGetLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [initialValue, setInitialValue] = useState<string | null>(null);
  const redirect = () => navigate("/itin");

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    setGetLoading(true);
    setLoading(true);
    try {
      const response = await ApiHelperFunction({
        url: `/get-itin-data/?order_id=${id}`,
        method: "GET",
        headers: {
          Authorization: token ? `${token}` : "",
        },
      });

      if (response?.data?.status === 1) {
        const hasUsCompany = response?.data?.data?.has_us_company;

        const formattedValue = hasUsCompany ? "true" : "false";

        setData((prev: any) => ({
          ...prev,
          has_us_company: formattedValue,
        }));
        setGetLoading(true);
        setInitialValue(formattedValue);
      }
      setLoading(false);
    } catch (error: any) {
      setGetLoading(true);
      if (error?.status >= 400) {
        await dispatch<any>(
          setToast({
            message:
              error?.data?.statusText ||
              (typeof error?.data?.detail === "string" &&
                error?.data?.detail) ||
              "Something went wrong!",
            type: "error",
            title: "Error",
          }),
        );
        const timeout = setTimeout(() => {
          navigate("/itin");
        }, 2000);

        return () => clearTimeout(timeout);
      }

      setLoading(false);
    } finally {
      setGetLoading(false);
    }
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(event);
    setErrorMessage("");
    console.log("Updated Payload:", {
      ...payload,
      [event.target.name]: event.target.value,
    });
  };

  useEffect(() => {
    setData(data);
  }, [data]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!payload?.has_us_company) {
      setErrorMessage("Select an option");
      setLoading(false);
      return;
    }

    const isUpdating = location.pathname.includes("edit");

    if (payload?.has_us_company === initialValue) {
      console.log("No changes detected. Skipping API call.");
      if (stepperState.setActiveStep) {
        stepperState.setActiveStep(stepperState.currentStepIdx + 1, true);
      }
      return;
    }

    // Construct API Payload
    const apiPayload = {
      has_us_company: payload?.has_us_company,
      order_id: id,
      ...(isUpdating && { is_updating: "yes" }), // Add is_updating: "yes" if editing
    };
    // if (payload?.has_us_company === initialValue) {
    //   console.log("No changes detected. Skipping API call.");
    //   if (stepperState.setActiveStep) {
    //     stepperState.setActiveStep(stepperState.currentStepIdx + 1, true);
    //   }
    //   return;
    // }

    // const apiPayload = { has_us_company: payload?.has_us_company };

    try {
      await dispatch<any>(createUpdateItin(apiPayload)).unwrap();

      if (stepperState.setData) {
        stepperState.setData(stepperState.currentStepIdx, apiPayload, false);
      }
      if (stepperState.setActiveStep) {
        stepperState.setActiveStep(stepperState.currentStepIdx + 1, true);
      }

      setInitialValue(payload?.has_us_company);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  return getLoading ? (
    // <Box
    //      sx={{
    //        display: "flex",
    //        alignItems: "center",
    //        justifyContent: "center",
    //        height: "100vh",
    //      }}
    //    >
    //      <CircularProgress />
    //    </Box>
    <ListLoadingNew />
  ) : (
    <Box sx={{ height: "calc(100vh - 120px)" }}>
      <TitleBar
        title="Choose Your Business Setup Option"
        description="Please select the option that best describes your current situation."
      />
      <form onSubmit={handleSubmit}>
        <FormControl
          sx={{ width: "100%", paddingX: 1, paddingY: 1, marginTop: "20px" }}
        >
          <RadioGroup
            name="has_us_company"
            value={payload?.has_us_company ?? ""}
            onChange={handleRadioChange}
          >
            <StyledFormControlLabel
              value="true"
              control={<Radio />}
              label="I have a US Company and EIN"
            />
            <StyledFormControlLabel
              value="false"
              control={<Radio />}
              label="I don’t have a US company and EIN"
            />
          </RadioGroup>
        </FormControl>

        {errorMessage && (
          <Box sx={{ color: "red", marginBottom: 2 }}>{errorMessage}</Box>
        )}

        <StepSubmitter
          label={"Next"}
          showPrevious={false}
          showNext={true}
          loading={loading}
        />
      </form>
    </Box>
  );
};
